import React from 'react';
import EDTypography from '../../../../BaseComponents/Typography/EDTypography';
import { ApolloClient, ApolloConsumer } from '@apollo/client';
import { IUIUrval } from '../../../../Types/UrvalTypes';
import {
  IUrvalHitIndicatorData,
  IUrvalHitIndicatorVariables,
  IDetaljurvalHitIndicatorData,
  IDetaljurvalHitIndicatorVariables,
  getUrvalHitIndicatorQuery,
  getdetaljurvalHitIndicatorQuery,
} from './Queries/UrvalHitIndicatorQuery';
import memoizeOne from 'memoize-one';
import { DocumentNode } from 'graphql';
import ActiveDatabaseName from '../../../../Features/Routing/ActiveDatabaseName';
import debounce from 'debounce';
import { tryConvertUIUrvalToTEDUrval } from '../../Utils/convertUIUrvalToTEDUrval';
import Logger from '../../../../Utils/Logger';
import { useIsExtraSmallScreen } from '../../../../Hooks/useFullscreenView';
import { IClientBusinessModel } from '../../../../Types/ClientModelTypes';

interface IProps {
  urval?: IUIUrval;
  collection: IClientBusinessModel;
  detaljurvalInfo?: IDetaljurvalInfo;
  routineID?: number;
  skip?: boolean;
}

interface IInnerProps extends IProps {
  apolloClient: ApolloClient<any>;
  query: DocumentNode;
  dbName: string | null;
  isExtraSmallScreen: boolean;
}

interface IState {
  hits?: number;
  errorMessage?: string;
}

export interface IDetaljurvalInfo {
  id: string;
  associationName: string;
  collectionName: string;
}

export class UrvalHitIndicator extends React.Component<IInnerProps, IState> {
  debouncedUpdateHits: () => void;
  mounted: boolean;

  constructor(props: IInnerProps) {
    super(props);
    this.state = { errorMessage: undefined, hits: undefined };

    this.debouncedUpdateHits = debounce(this.updateHits, 500);
    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;

    if (!this.props.skip) {
      this.updateHits();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  setError = () => {
    if (this.mounted) {
      this.setState({
        errorMessage: 'Kunde inte hämta antal träffar',
      });
    }
  };

  updateHits = () => {
    if (!this.mounted) {
      return;
    }

    const { urval, apolloClient, query, dbName, collection } = this.props;

    const convertedUrval = tryConvertUIUrvalToTEDUrval(urval);

    if (dbName == null) {
      this.setError();
      Logger.error('dbName is null');
      return;
    }

    apolloClient
      .query<
        IUrvalHitIndicatorData | IDetaljurvalHitIndicatorData,
        IUrvalHitIndicatorVariables | IDetaljurvalHitIndicatorVariables
      >({
        query,
        variables:
          this.props.detaljurvalInfo != null
            ? { id: this.props.detaljurvalInfo.id, urval: convertedUrval }
            : { dbName, urval: convertedUrval },
        fetchPolicy: 'network-only',
      })
      .then(({ data, errors }) => {
        if (!errors) {
          const connection =
            this.props.detaljurvalInfo == null
              ? (data as IUrvalHitIndicatorData).db &&
                (data as IUrvalHitIndicatorData).db?.[
                  `${collection.CollectionName}_Connection`
                ]
              : (data as IDetaljurvalHitIndicatorData).node?._Associations?.[
                  `${this.props.detaljurvalInfo.associationName}_Connection`
                ];

          const numberOfHits = connection?.totalCount;

          if (this.mounted) {
            this.setState({ hits: numberOfHits, errorMessage: undefined });
          }
        } else {
          this.setError();
        }
      })
      .catch(error => {
        this.setError();
      });
  };

  componentDidUpdate(prevProps: IInnerProps, prevState: IState) {
    const urvalHasChanged = prevProps.urval !== this.props.urval;

    if (urvalHasChanged && !this.props.skip) {
      this.debouncedUpdateHits();
    }
  }

  render() {
    const { hits, errorMessage } = this.state;
    const { collection } = this.props;

    if (errorMessage) {
      return (
        <EDTypography
          variant="subtitle2"
          color="error"
          testId="UrvalHitIndicator"
        >
          {errorMessage}
        </EDTypography>
      );
    } else {
      return (
        <EDTypography variant="subtitle2" testId="UrvalHitIndicator">
          {`${hits == null ? '..' : hits} ${
            hits === 1
              ? collection.RootType.SOLabel.toLowerCase()
              : collection.RootType.POLabel.toLowerCase()
          }${this.props.isExtraSmallScreen ? '' : ' med detta urval'}`}
        </EDTypography>
      );
    }
  }
}

/**
 * Visar en text som klargör hur många träffar det nuvarande urvalet fick i datan.
 */
const EnhancedUrvalHitIndicator: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  let query: DocumentNode;

  const isExtraSmallScreen = useIsExtraSmallScreen();

  if (props.detaljurvalInfo != null) {
    const memoizedGetUrvalHitIndicatorQuery = memoizeOne(
      getdetaljurvalHitIndicatorQuery
    );
    query = memoizedGetUrvalHitIndicatorQuery(
      props.detaljurvalInfo.collectionName,
      props.detaljurvalInfo.associationName
    );
  } else {
    const memoizedGetUrvalHitIndicatorQuery = memoizeOne(
      getUrvalHitIndicatorQuery
    );
    query = memoizedGetUrvalHitIndicatorQuery(
      props.collection.CollectionName,
      props.routineID
    );
  }

  return (
    <ActiveDatabaseName>
      {(databaseName: string | null) => (
        <ApolloConsumer>
          {apolloClient => (
            <UrvalHitIndicator
              apolloClient={apolloClient}
              query={query}
              dbName={databaseName}
              isExtraSmallScreen={isExtraSmallScreen}
              {...props}
            />
          )}
        </ApolloConsumer>
      )}
    </ActiveDatabaseName>
  );
};

export default EnhancedUrvalHitIndicator;
